import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ExternalLink, X } from 'lucide-react'

/**
 * Citation popup component that shows source details on hover/click
 */
const CitationPopup = ({ citationNumber, source, children, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef(null)
  const popupRef = useRef(null)
  const timeoutRef = useRef(null)

  const showPopup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(true)
  }

  const hidePopup = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 300) // Delay to allow moving to popup
  }

  const cancelHide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  // Calculate popup position
  useEffect(() => {
    if (isVisible && triggerRef.current && popupRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const popupRect = popupRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let top = triggerRect.bottom + 8
      let left = triggerRect.left

      // Adjust if popup goes off screen
      if (left + popupRect.width > viewportWidth) {
        left = viewportWidth - popupRect.width - 16
      }
      if (left < 16) {
        left = 16
      }
      if (top + popupRect.height > viewportHeight) {
        top = triggerRect.top - popupRect.height - 8
      }

      setPopupPosition({ top, left })
    }
  }, [isVisible])

  if (!source) {
    return children
  }

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-block cursor-pointer text-blue-400 hover:text-blue-300 transition-colors font-medium"
        onMouseEnter={showPopup}
        onMouseLeave={hidePopup}
        onClick={showPopup}
      >
        {children}
      </span>
      
      {isVisible && (
        <div
          ref={popupRef}
          className="fixed z-50 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-w-sm w-80"
          style={{
            top: `${popupPosition.top}px`,
            left: `${popupPosition.left}px`
          }}
          onMouseEnter={cancelHide}
          onMouseLeave={hidePopup}
        >
          <div className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                  {citationNumber}
                </div>
                <h3 className="text-white text-sm font-medium line-clamp-2">
                  {source.title || 'Source'}
                </h3>
              </div>
              <button
                onClick={() => setIsVisible(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {source.snippet && (
              <p className="text-gray-300 text-xs leading-relaxed mb-3 line-clamp-4">
                {source.snippet}
              </p>
            )}
            
            {source.url && (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-blue-400 hover:text-blue-300 text-xs transition-colors"
              >
                <span>Visit Source</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </>
  )
}

CitationPopup.propTypes = {
  citationNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  source: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    snippet: PropTypes.string
  }),
  children: PropTypes.node.isRequired,
  position: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
}

export default CitationPopup